import { useScrollReveal } from "@/hooks/useScrollReveal";

interface Brand {
  /** Brand referenced as a case-study subject (nominative use). */
  name: string;
  /** Placeholder wordmark path under public/assets/brands/ — swap for a
   *  properly-licensed logo later by overwriting the file. */
  logo: string;
}

/**
 * Brands are case-study/analysis subjects, NOT clients or partners. The
 * images are neutral wordmark PLACEHOLDERS (see scripts/gen-brand-
 * placeholders.mjs), not the real trademarked logos. Disclaimer below makes
 * the non-affiliation explicit.
 */
const brands: Brand[] = [
  { name: "Tuku Coffee", logo: "/assets/brands/panduankafe_logo_kopi-tuku.webp" },
  { name: "Kopi Kenangan", logo: "/assets/brands/panduankafe_logo_kopi-kenangan.webp" },
  { name: "Fore Coffee", logo: "/assets/brands/panduankafe_logo_fore-coffee.webp" },
  { name: "Tomoro Coffee", logo: "/assets/brands/panduankafe_logo_tomoro-coffee.webp" },
  { name: "Teman Nongkrong Coffee", logo: "/assets/brands/panduankafe_logo_teman-nongkrong.webp" },
  { name: "Kopitiam", logo: "/assets/brands/panduankafe_logo_kopitiam.webp" },
  { name: "Janji Jiwa Coffee", logo: "/assets/brands/panduankafe_logo_janji-jiwa.webp" },
  { name: "Maxx Coffee", logo: "/assets/brands/panduankafe_logo_maxx-coffee.webp" },
  { name: "Kopi Dari Hati", logo: "/assets/brands/panduankafe_logo_kopi-dari-hati.webp" },
  { name: "Warkop Naik Kelas", logo: "/assets/brands/panduankafe_logo_warkop-naik-kelas.webp" },
  { name: "Excelso", logo: "/assets/brands/panduankafe_logo_excelso.webp" },
  { name: "Kopi Jago", logo: "/assets/brands/panduankafe_logo_kopi-jago.webp" },
  { name: "Kopi Nako", logo: "/assets/brands/panduankafe_logo_kopi-nako.webp" },
  { name: "Calf Coffee", logo: "/assets/brands/panduankafe_logo_calf-coffee.webp" },
  { name: "Arah Coffee", logo: "/assets/brands/panduankafe_logo_arah-coffee.webp" },
  { name: "Starbucks", logo: "/assets/brands/panduankafe_logo_starbucks.webp" },
  { name: "Kopi Kulo", logo: "/assets/brands/panduankafe_logo_kopi-kulo.webp" },
  { name: "Coffee Toffee", logo: "/assets/brands/panduankafe_logo_coffee-toffee.webp" },
];

const BODY_COPY =
  "Tidak perlu trial & error. Kami memiliki playbook operasional dari berbagai kiblat bisnis kopi—mulai dari skala gerobakan, tech-enabled coffee chain, hingga boutique cafe estetik.";

const BrandCard = ({ brand }: { brand: Brand }) => (
  <div className="mx-4 flex h-24 w-48 shrink-0 items-center justify-center rounded-2xl bg-white px-7 shadow-elevated md:h-28 md:w-56">
    <img
      src={brand.logo}
      alt={brand.name}
      title={brand.name}
      width={224}
      height={72}
      loading="lazy"
      decoding="async"
      className="max-h-12 w-full object-contain md:max-h-14"
    />
  </div>
);

const MarqueeRow = ({
  items,
  reverse = false,
  duration,
}: {
  items: Brand[];
  reverse?: boolean;
  duration: string;
}) => (
  <div className="marquee-row flex overflow-hidden py-3">
    <div
      className={`marquee-track${reverse ? " reverse" : ""}`}
      style={{ ["--marquee-duration" as string]: duration }}
    >
      {/* Primary group: real, screen-reader-readable list */}
      <ul className="marquee-group list-none p-0">
        {items.map((brand) => (
          <li key={brand.name}>
            <BrandCard brand={brand} />
          </li>
        ))}
      </ul>
      {/* Duplicate group: visual seamless-loop only, hidden from a11y tree */}
      <ul className="marquee-group list-none p-0" aria-hidden="true">
        {items.map((brand) => (
          <li key={`${brand.name}-dup`}>
            <BrandCard brand={brand} />
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const MultiBrandSection = () => {
  const sectionRef = useScrollReveal();

  const rowOne = brands.slice(0, 9);
  const rowTwo = brands.slice(9);

  return (
    <section
      id="multi-brand"
      ref={sectionRef}
      className="overflow-hidden bg-espresso py-24 text-cream md:py-32"
    >
      <div className="container mx-auto">
        <div className="reveal-on-scroll mx-auto max-w-4xl text-center">
          <p className="mb-6 text-sm font-bold uppercase tracking-[0.35em] text-terracotta">
            Konsultasi Multi-Brand
          </p>
          <h2 className="font-serif text-4xl font-medium leading-[1.1] text-cream sm:text-5xl md:text-6xl">
            Kami Membedah Strategi{" "}
            <em className="italic text-terracotta">15+ Brand F&amp;B Raksasa</em>{" "}
            untuk Kafe Anda.
          </h2>
          <p className="mx-auto mt-7 max-w-2xl text-xl leading-relaxed text-cream/90 md:text-2xl">
            {BODY_COPY}
          </p>
        </div>
      </div>

      {/* Educational/benchmark framing — these are study subjects, not clients. */}
      <div className="container mx-auto">
        <p className="reveal-on-scroll mt-16 text-center text-sm font-medium tracking-wide text-cream/60">
          Merek &amp; Brand yang Kami Bedah Sebagai Studi Kasus Operasional:
        </p>
      </div>

      {/* Full-bleed marquee — obvious, continuous, communicates "multi-brand"
          at a glance. Edges fade via overlay gradients (NOT mask-image —
          mask on an animated ancestor freezes the loop on iOS Safari). */}
      <div
        className="reveal-on-scroll relative mt-8 flex flex-col gap-5"
        style={{ transitionDelay: "120ms" }}
      >
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-espresso to-transparent md:w-32" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-espresso to-transparent md:w-32" />

        {/* Row 1 scrolls Left-to-Right, Row 2 Right-to-Left. */}
        <MarqueeRow items={rowOne} reverse duration="45s" />
        <MarqueeRow items={rowTwo} duration="55s" />
      </div>

      <div className="container mx-auto">
        <p className="reveal-on-scroll mx-auto mt-8 max-w-3xl text-center text-[10px] leading-relaxed text-cream/60">
          Disclaimer: Semua logo dan merek dagang adalah milik mutlak dari
          masing-masing pemilik hak cipta. Panduan Kafe tidak memiliki afiliasi
          resmi, kemitraan, atau hubungan komersial langsung dengan brand yang
          disebutkan di atas. Penggunaan logo murni hanya ditujukan sebagai referensi
          studi kasus dan edukasi manajemen operasional.
        </p>
      </div>
    </section>
  );
};

export default MultiBrandSection;
